import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const API_BASE = 'https://cvanalyzerback.osc-fr1.scalingo.io/api';

const GuideDynamic = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [guide, setGuide] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        fetch(`${API_BASE}/guides/${slug}`)
            .then(res => {
                if (!res.ok) throw new Error('introuvable');
                return res.json();
            })
            .then(data => { setGuide(data); setLoading(false); })
            .catch(() => { navigate('/guides', { replace: true }); });
    }, [slug, navigate]);

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-200 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
        );
    }

    if (!guide) return null;

    const howToSchema = {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: guide.heroTitle,
        description: guide.metaDescription,
        step: guide.howToSteps.map(s => ({
            '@type': 'HowToStep',
            name: s.name,
            text: s.text,
        })),
    };

    const articleSchema = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: guide.metaTitle,
        description: guide.metaDescription,
        author: { '@type': 'Organization', name: 'CV Analyse', alternateName: 'CV Analyzer' },
        publisher: { '@type': 'Organization', name: 'CV Analyse', alternateName: 'CV Analyzer' },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `https://www.cv-analyse.fr/guide/${slug}` },
        dateModified: guide.updatedAt || undefined,
        datePublished: guide.createdAt || guide.updatedAt || undefined,
    };

    return (
        <div className="min-h-screen bg-slate-200 pt-16">
            <Helmet>
                <title>{guide.metaTitle}</title>
                <meta name="description" content={guide.metaDescription} />
                {guide.keywords && <meta name="keywords" content={guide.keywords} />}
                <link rel="canonical" href={`https://www.cv-analyse.fr/guide/${slug}`} />
                <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
            </Helmet>

            <div className="bg-slate-900 text-white">
                <nav className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 pb-3">
                    <ol className="flex items-center gap-2 text-sm text-slate-300">
                        <li><Link to="/" className="hover:text-white">Accueil</Link></li>
                        <li className="text-slate-500">/</li>
                        <li><Link to="/guides" className="hover:text-white">Guides</Link></li>
                        <li className="text-slate-500">/</li>
                        <li className="text-white truncate">{guide.heroTitle}</li>
                    </ol>
                </nav>
                <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-10">
                    <h1 className="text-3xl sm:text-4xl font-semibold mb-3 leading-tight">
                        {guide.heroTitle}
                    </h1>
                    <p className="text-slate-300 max-w-2xl leading-relaxed">
                        {guide.intro}
                    </p>
                    {guide.updatedAt && (
                        <p className="text-slate-400 text-xs mt-4">
                            Mis à jour le {new Date(guide.updatedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                    )}
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
                <div className="cta-band text-center mb-8">
                    <p className="font-semibold text-lg mb-1">Votre CV est-il optimisé pour ce poste ?</p>
                    <p className="text-slate-300 text-sm mb-4">Analysez-le en 2 minutes — score et 3 conseils offerts.</p>
                    <Link to="/" className="btn-primary">
                        Analyser mon CV
                    </Link>
                </div>

                <div className="space-y-4">
                    {guide.sections.map((section, i) => (
                        <div key={i} className="card-surface p-5 sm:p-7">
                            <h2 className="text-xl font-semibold text-slate-900 mb-3">{section.title}</h2>
                            <p className="text-slate-700 leading-relaxed whitespace-pre-line">{section.content}</p>
                        </div>
                    ))}
                </div>

                {guide.howToSteps.length > 0 && (
                    <div className="mt-6 card-surface p-5 sm:p-7">
                        <h2 className="text-xl font-semibold text-slate-900 mb-5">Comment procéder — étape par étape</h2>
                        <ol className="space-y-4">
                            {guide.howToSteps.map((step, i) => (
                                <li key={i} className="flex items-start gap-4">
                                    <span className="w-8 h-8 bg-blue-600 text-white rounded-lg flex items-center justify-center font-semibold text-sm shrink-0">
                                        {i + 1}
                                    </span>
                                    <div>
                                        <p className="font-semibold text-slate-800">{step.name}</p>
                                        <p className="text-slate-600 text-sm mt-0.5">{step.text}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                )}

                <div className="cta-band text-center mt-8">
                    <p className="text-lg font-semibold mb-2">Prêt à optimiser votre CV ?</p>
                    <p className="text-slate-300 text-sm mb-4">
                        Score, mots-clés manquants et suggestions personnalisées.
                    </p>
                    <Link to="/" className="btn-primary">
                        Commencer gratuitement
                    </Link>
                </div>

                <div className="mt-6 text-center">
                    <Link to="/guides" className="text-blue-700 hover:text-blue-800 text-sm font-medium">
                        ← Voir tous les guides CV
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default GuideDynamic;
